/**
 * The "Information" block from the Advanced blood-test popup (`ProductUspsModal`),
 * extracted so it can be reused as-is in the membership popup — membership
 * includes the same advanced blood test, so the same info (icon · label ·
 * value rows) applies. Self-fetches the product's USP `info` from Supabase,
 * matching ProductUspsModal's query, and renders nothing if there's no data.
 */
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client.ts";
import { resolveProductKey } from "@/locales";
import { BookingLanguage } from "@/types/booking";
import { PacemakerIcon } from "@/components/icons/PacemakerIcon";
import { PersonHeartIcon } from "@/components/icons/PersonHeartIcon";
import { TubeIcon } from "@/components/icons/TubeIcon";
import { TimeIcon } from "@/components/icons/TimeIcon";
import { RadiationIcon } from "@/components/icons/RadiationIcon";
import { ContrastIcon } from "@/components/icons/ContrastIcon";
import { PrepIcon } from "@/components/icons/PrepIcon";
import { UserIcon } from "@/components/icons/UserIcon";
import { isImagingInfoRow } from "@/utils/isBloodTestProduct";

const iconMap: Record<string, React.ElementType> = {
  pacemaker: PacemakerIcon,
  person_heart: PersonHeartIcon,
  tube: TubeIcon,
  time: TimeIcon,
  radiation: RadiationIcon,
  contrast: ContrastIcon,
  prep: PrepIcon,
  user: UserIcon,
};

interface InfoRow {
  labelKey: string;
  valueKey: string;
  icon: string;
}

interface ProductInfoSectionProps {
  productId: string;
  language: BookingLanguage;
  useCase?: "package_dialog_content" | "addon_dialog_content";
}

export const ProductInfoSection = ({
  productId,
  language,
  useCase = "package_dialog_content",
}: ProductInfoSectionProps) => {
  const [info, setInfo] = useState<InfoRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data } = (await (supabase as any)
          .from("usp")
          .select("usp")
          .eq("product_id", productId)
          .eq("use_case", useCase)
          .maybeSingle()) as { data: { usp: unknown } | null };
        if (cancelled) return;
        const rows = (data?.usp as { info?: InfoRow[] } | null)?.info ?? null;
        // Always a blood test here: no radiation or contrast rows (AHD-13466).
        setInfo(rows && rows.filter((row) => !isImagingInfoRow(row)));
      } catch {
        if (!cancelled) setInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId, useCase]);

  if (!info || info.length === 0) return null;

  const resolve = (key: string) => resolveProductKey(language, key);
  const renderIcon = (iconName: string) => {
    const Icon = iconMap[iconName];
    return Icon ? (
      <Icon className="mt-0.5 h-6 w-6 flex-shrink-0 text-[#2d2d2d]" />
    ) : null;
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="px-4 py-2">
        <p className="text-base font-medium text-black">
          {resolve("packageDialog.information")}
        </p>
      </div>
      <div className="rounded-2xl bg-[#f6f6f6] p-4">
        <div className="flex flex-col gap-3">
          {info.map((row, i) => (
            <div key={i} className="flex items-start gap-3">
              {renderIcon(row.icon)}
              <div className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-baseline">
                <span className="text-base font-medium text-[#2d2d2d] sm:flex-shrink-0 sm:font-normal">
                  {resolve(row.labelKey)}
                </span>
                <div className="hidden flex-1 sm:block" />
                <span className="text-base font-normal leading-6 text-[#2d2d2d] sm:text-right">
                  {resolve(row.valueKey)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
